import Menu from "@mui/material/Menu";
import { DateTime } from "luxon";
import React, { Dispatch, SetStateAction, useState } from "react";
import { AiFillThunderbolt } from "react-icons/ai";
import { BiPlay, BiStop } from "react-icons/bi";
import { CgPlayBackwards, CgPlayForwards } from "react-icons/cg";
import { FaRegKeyboard } from "react-icons/fa6";
import { LuZoomIn, LuZoomOut } from "react-icons/lu";
import IconButton from "./IconButton";
import VideoPlayerTriage from "./VideoPlayerTriage";

interface VideoPlayerControlsProps {
  createTriageLoading?: boolean;
  currentTimestamp: number;
  playbackSpeed: number;
  disabled: boolean;
  displayOnly: boolean;
  backwardRecordingDisabled: boolean;
  forwardRecordingDisabled: boolean;
  skipInactivity: boolean;
  isPlaying: boolean;
  hideTriageButton?: boolean;
  timezone: string;
  changeSpeed: (speed: number) => void;
  handleBackwardRecording: () => void;
  handleCreateTriage: () => void;
  handleForwardRecording: () => void;
  setTimezone: Dispatch<SetStateAction<string>>;
  togglePlayPause: () => void;
  toggleSkipInactivity: () => void;
  toggleDisplayMode: () => void;
}

const VideoPlayerControls: React.FC<VideoPlayerControlsProps> = ({
  createTriageLoading,
  currentTimestamp,
  playbackSpeed,
  disabled,
  displayOnly,
  backwardRecordingDisabled,
  forwardRecordingDisabled,
  skipInactivity,
  isPlaying,
  hideTriageButton,
  timezone,
  changeSpeed,
  handleBackwardRecording,
  handleCreateTriage,
  handleForwardRecording,
  setTimezone,
  togglePlayPause,
  toggleSkipInactivity,
  toggleDisplayMode,
}) => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const withConfirmation = (cb: () => void) => () => {
    if (
      displayOnly ||
      confirm(
        "Are you sure you want to change the recording? You have unsaved changes.",
      )
    ) {
      cb();
    }
  };

  const [timeStr, ampm] = DateTime.fromMillis(currentTimestamp)
    .setZone(timezone)
    .toFormat("hh:mm:ss a")
    .split(" ");

  return (
    <div className='relative'>
      <div className='flex items-center justify-between w-full'>
        <div className='flex gap-2'>
          <IconButton
            onClick={withConfirmation(handleBackwardRecording)}
            size='text-base'
            icon={CgPlayBackwards}
            disabled={backwardRecordingDisabled}
            dataTestId='backward'
          />
          <IconButton
            onClick={togglePlayPause}
            size='text-base'
            flag={isPlaying}
            icon={BiPlay}
            trueIcon={BiStop}
            falseIcon={BiPlay}
            disabled={disabled}
            dataTestId='playPause'
          />
          <IconButton
            onClick={withConfirmation(handleForwardRecording)}
            size='text-base'
            icon={CgPlayForwards}
            disabled={forwardRecordingDisabled}
            dataTestId='forward'
          />
          <div className='w-[88px] text-sm font-medium'>
            <div
              className='w-full flex justify-between'
              data-testid='playerCurrentTime'
            >
              <span>{timeStr}</span>
              <span>{ampm}</span>
            </div>
          </div>
        </div>
        {!hideTriageButton && (
          <VideoPlayerTriage
            createTriageLoading={createTriageLoading}
            displayOnly={displayOnly}
            handleCreateTriage={handleCreateTriage}
            toggleDisplayMode={toggleDisplayMode}
          />
        )}
        <div className='ml-auto flex justify-end gap-2'>
          <select
            value={playbackSpeed}
            onChange={(e) => changeSpeed(Number(e.target.value))}
            className={`btn btn-primary bg-inherit ${
              disabled ? "text-gray-400" : "text-black"
            }`}
            disabled={disabled}
            data-testid='playerSpeedSelect'
          >
            <option value={1}>1x</option>
            <option value={2}>2x</option>
            <option value={4}>4x</option>
            <option value={8}>8x</option>
          </select>
          <IconButton
            onClick={toggleSkipInactivity}
            size='text-base'
            flag={skipInactivity}
            icon={AiFillThunderbolt}
            disabled={disabled}
            tooltip={`${skipInactivity ? "Show" : "Hide"} Inactivity`}
            dataTestId='inactivity'
          />
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            size='text-base'
            icon={FaRegKeyboard}
            dataTestId='keyboardShortcuts'
          />
          <Menu
            data-testid='keyboardShortcutsMenu'
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={() => setAnchorEl(null)}
            classes={{ paper: "text-sm px-3 !text-slate-400 font-medium" }}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            transformOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <div data-testid='zoomIn' className='flex gap-1 items-center'>
              <LuZoomIn className='text-primary-blue-100' /> cmd & shift [+]
            </div>
            <div data-testid='zoomOut' className='flex gap-1 items-center'>
              <LuZoomOut className='text-primary-blue-100' /> cmd & shift [-]
            </div>
          </Menu>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerControls;
